/**
 * Phase 10 — multi-sample Pk trajectory viewer.
 *
 * Renders many Monte Carlo samples in a single Plotly scene with
 * success / fail colour coding and an optional Pk uncertainty cloud
 * (5th–95th percentile envelope of the interceptor altitude bracket).
 *
 * This component is INTENTIONALLY separate from ``Trajectory3D.jsx``.
 * The Phase 8c contract for the 6 public demos depends on
 * ``Trajectory3D``'s prop / data shape — extending that file directly
 * would risk breaking the public ``/demos`` viewer. ``PkTrajectory3D``
 * is purpose-built for the multi-sample success/fail use case. They
 * share a Plotly scene-styling helper (defined here) and produce
 * visually consistent output.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatApiError } from "@/lib/formatApiError";
import { buildVehicleMarkers } from "@/lib/viz3d";
import pkCampaigns from "@/api/pkCampaigns";

const SUCCESS_COLOR = "#34d399"; // emerald-400
const FAIL_COLOR = "#f87171"; // red-400
const TARGET_COLOR = "#fbbf24"; // amber-400
const CLOUD_COLOR = "rgba(99, 102, 241, 0.18)"; // indigo

// Fall back to a no-op renderer in non-browser environments (RTL).
let PlotlyImpl = null;
async function getPlotly() {
    if (PlotlyImpl) return PlotlyImpl;
    try {
        const mod = await import("plotly.js-dist-min");
        PlotlyImpl = mod.default || mod;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[PkTrajectory3D] plotly load failed:", e);
        PlotlyImpl = null;
    }
    return PlotlyImpl;
}

/** Top-level component used by PkCampaignDetailPage. */
export default function PkTrajectory3D({
    campaignId,
    samples = [], // [{sample_idx, success, failure_mode, ...}]
    showUncertaintyCloud = true,
    showAllSamples = true,
    activeIdx = null,
    height = 480,
    fetchTrajectory = null,   // Phase 17.8 — injectable client (defaults to legacy pkCampaigns)
    "data-testid": dataTestId = "pk-trajectory-3d",
}) {
    const containerRef = useRef(null);
    const [trajectories, setTrajectories] = useState({}); // sample_idx → traj JSON
    const [loadStatus, setLoadStatus] = useState("idle");
    const [err, setErr] = useState("");

    // Pre-fetch every sample trajectory in parallel. ``trajectory_path``
    // is the relative path the backend emits; we resolve it via the
    // authed pkCampaigns.sampleTrajectory client (Phase 17.8 — or any
    // injected ``fetchTrajectory(campaignId, sampleIdx)`` callback so
    // the unified campaigns surface can reuse this viewer).
    useEffect(() => {
        if (!campaignId || !samples.length) return;
        let cancelled = false;
        setLoadStatus("loading");
        setErr("");
        const getter = fetchTrajectory
            || ((cid, idx) => pkCampaigns.sampleTrajectory(cid, idx));
        Promise.all(
            samples.map((s) =>
                getter(campaignId, s.sample_idx)
                    .then((data) => [s.sample_idx, data])
                    .catch((e) => [s.sample_idx, { __error: formatApiError(e) }]),
            ),
        )
            .then((entries) => {
                if (cancelled) return;
                const next = {};
                let firstErr = "";
                for (const [idx, data] of entries) {
                    if (data?.__error && !firstErr) firstErr = data.__error;
                    if (!data?.__error) next[idx] = data;
                }
                setTrajectories(next);
                if (firstErr && Object.keys(next).length === 0) {
                    setErr(firstErr);
                    setLoadStatus("error");
                } else {
                    setLoadStatus("ready");
                }
            })
            .catch((e) => {
                if (cancelled) return;
                setErr(formatApiError(e, "Failed to load trajectories"));
                setLoadStatus("error");
            });
        return () => { cancelled = true; };
        // Phase 14.8 W5 — `samples` is rebuilt by the parent on each
        // render but its content (sample_idx list) only effectively
        // changes when `samples.length` does; depending on `samples`
        // would re-fire the heavy fetch loop on every parent render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId, samples.length]);

    // Build Plotly traces from the pre-fetched trajectories.
    // Phase 17.8 follow-up — Item 2. Trajectory lines are
    // subordinate (thin / low-opacity); vehicle markers
    // (interceptor launch + CPA, target start + CPA) are the
    // primary visual element. Legend rows for the 4 vehicle states
    // are contributed by the FIRST sample only (`legendOnFirst`)
    // so the legend stays compact for N-sample campaigns.
    const traces = useMemo(() => {
        if (loadStatus !== "ready") return [];
        const out = [];
        let plotedLegend = false;
        let firstActiveIdx = null;
        for (const s of samples) {
            if (!trajectories[s.sample_idx]?.samples?.[0]) continue;
            firstActiveIdx = s.sample_idx;
            break;
        }
        // Per-sample interceptor lines, coloured by success.
        for (const s of samples) {
            const traj = trajectories[s.sample_idx];
            if (!traj?.samples?.[0]) continue;
            const isActive = activeIdx === s.sample_idx;
            if (!showAllSamples && !isActive) continue;
            const inter = traj.samples[0].interceptor;
            const target = traj.samples[0].target;
            const colour = s.success ? SUCCESS_COLOR : FAIL_COLOR;
            // Subordinated trajectory line (thinner + lower opacity
            // than pre-17.8). The vehicle markers below dominate
            // the visual; the line gives motion context only.
            out.push({
                type: "scatter3d",
                mode: "lines",
                name: `Sample ${s.sample_idx} — ${s.success ? "intercept" : s.failure_mode}`,
                x: inter.x, y: inter.y, z: inter.z,
                line: { color: colour, width: isActive ? 4 : 1.2 },
                opacity: isActive ? 0.95 : (activeIdx == null ? 0.45 : 0.18),
                showlegend: false,
                hovertemplate: `Sample ${s.sample_idx} (${s.success ? "✔" : "✗"})<extra></extra>`,
            });
            // Phase 19 demo follow-on — when the trajectory blob
            // carries the multi-agent ``threats`` + ``interceptors``
            // companion block (Saudi East Coast / netted scenarios),
            // overlay all the threat + interceptor polylines so the
            // viewer renders "10 threats teal-blue + N interceptors
            // navy + hit/miss glyph at terminal" instead of just the
            // single representative pair. Only render once, on the
            // first sample, otherwise the legend balloons.
            if (
                s.sample_idx === firstActiveIdx
                && traj.multiAgent
            ) {
                const MA_THREAT_COLOUR = "#4FB6C6";   // defence teal
                const MA_INT_HIT = "#0B2545";          // defence navy
                const MA_INT_MISS = "#5A6B7C";         // steel-gray
                let threatLegendShown = false;
                let intHitLegendShown = false;
                let intMissLegendShown = false;
                for (const t of traj.multiAgent.threats) {
                    if (!t.x?.length) continue;
                    out.push({
                        type: "scatter3d", mode: "lines",
                        name: "Threat (multi-agent)",
                        x: t.x, y: t.y, z: t.z,
                        line: { color: MA_THREAT_COLOUR, width: 2.5 },
                        opacity: 0.85,
                        showlegend: !threatLegendShown,
                        legendgroup: "ma-threats",
                        hovertemplate: `Threat ${t.id} (${t.airframe})<extra></extra>`,
                    });
                    threatLegendShown = true;
                }
                for (const iv of traj.multiAgent.interceptors) {
                    if (!iv.x?.length) continue;
                    const colour = iv.success ? MA_INT_HIT : MA_INT_MISS;
                    const isHit = iv.success;
                    out.push({
                        type: "scatter3d", mode: "lines",
                        name: isHit
                            ? "Interceptor (hit)"
                            : "Interceptor (miss)",
                        x: iv.x, y: iv.y, z: iv.z,
                        line: { color: colour, width: 2.0 },
                        opacity: 0.75,
                        showlegend:
                            (isHit && !intHitLegendShown)
                            || (!isHit && !intMissLegendShown),
                        legendgroup: isHit ? "ma-int-hit" : "ma-int-miss",
                        hovertemplate: (
                            `Interceptor ${iv.slot_id} `
                            + `(${iv.battery_id}) pk=${iv.pk?.toFixed?.(2) ?? "—"} `
                            + `<extra></extra>`
                        ),
                    });
                    if (isHit) { intHitLegendShown = true; }
                    else { intMissLegendShown = true; }
                    // Terminal glyph at the interceptor's final point.
                    const lastIdx = iv.x.length - 1;
                    out.push({
                        type: "scatter3d", mode: "markers",
                        x: [iv.x[lastIdx]], y: [iv.y[lastIdx]], z: [iv.z[lastIdx]],
                        marker: {
                            size: 6,
                            symbol: isHit ? "diamond" : "x",
                            color: colour,
                            line: { color: "#FFFFFF", width: 1 },
                        },
                        showlegend: false,
                        hovertemplate: (
                            `${isHit ? "Intercept" : "Miss"} • `
                            + `${iv.slot_id} • pk=${iv.pk?.toFixed?.(2) ?? "—"}`
                            + `<extra></extra>`
                        ),
                    });
                }
            }
            // Target track on first sample only — nominally aligned.
            if (s.sample_idx === samples[0].sample_idx) {
                out.push({
                    type: "scatter3d",
                    mode: "lines",
                    name: "Target track",
                    x: target.x, y: target.y, z: target.z,
                    line: { color: TARGET_COLOR, width: 2, dash: "dash" },
                    opacity: 0.55,
                    showlegend: true,
                    hovertemplate: "Target track<extra></extra>",
                });
            }
            // Vehicle markers — interceptor launch + CPA, target
            // start + CPA. Legend contributed by the first sample
            // only (`showLegendOnly: true` on subsequent ones).
            const showLegendOnly = plotedLegend || s.sample_idx !== firstActiveIdx;
            const markers = buildVehicleMarkers(inter, target, {
                showLegendOnly,
                sampleIdx: s.sample_idx,
            });
            out.push(...markers);
            if (!showLegendOnly) {
                plotedLegend = true;
            }
        }
        // Uncertainty cloud — interceptor altitude envelope (5th–95th).
        if (showUncertaintyCloud) {
            const cloud = _buildAltitudeCloud(samples, trajectories);
            if (cloud) out.push(cloud);
        }
        return out;
    }, [samples, trajectories, loadStatus, activeIdx, showAllSamples, showUncertaintyCloud]);

    // Render Plotly once traces are ready.
    useEffect(() => {
        if (loadStatus !== "ready" || traces.length === 0) return;
        let cancelled = false;
        (async () => {
            const Plotly = await getPlotly();
            if (cancelled || !Plotly || !containerRef.current) return;
            await Plotly.react(
                containerRef.current,
                traces,
                {
                    paper_bgcolor: "rgba(0,0,0,0)",
                    plot_bgcolor: "rgba(0,0,0,0)",
                    margin: { l: 0, r: 0, t: 0, b: 0 },
                    scene: {
                        xaxis: { title: "East (m)", color: "#94a3b8" },
                        yaxis: { title: "North (m)", color: "#94a3b8" },
                        zaxis: { title: "Altitude (m)", color: "#94a3b8" },
                        bgcolor: "rgba(0,0,0,0)",
                    },
                    legend: { font: { color: "#cbd5e1" }, orientation: "h", y: -0.05 },
                    showlegend: true,
                },
                { responsive: true, displaylogo: false },
            );
        })();
        return () => { cancelled = true; };
    }, [traces, loadStatus]);

    if (loadStatus === "loading" || loadStatus === "idle") {
        return (
            <div data-testid={`${dataTestId}-loading`} style={{ height }} className="flex items-center justify-center">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }
    if (loadStatus === "error") {
        return (
            <div
                data-testid={`${dataTestId}-error`}
                className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200"
                style={{ minHeight: 120 }}
            >
                {err}
            </div>
        );
    }
    if (Object.keys(trajectories).length === 0) {
        return (
            <div data-testid={`${dataTestId}-no-data`} className="text-muted-foreground text-sm p-6">
                No trajectory artefacts persisted for this campaign yet.
            </div>
        );
    }
    return (
        <div
            data-testid={dataTestId}
            ref={containerRef}
            style={{ width: "100%", height }}
            className="rounded-md border border-ux-border-1 bg-ux-surface-1"
        />
    );
}

/** Build a single-trace 3D scatter for the 5th–95th altitude envelope
 * across all samples — the "Pk uncertainty cloud" the spec asks for.
 * Returns null when there's not enough data. */
function _buildAltitudeCloud(samples, trajectories) {
    const tracks = samples
        .map((s) => trajectories[s.sample_idx]?.samples?.[0]?.interceptor)
        .filter((tr) => tr && Array.isArray(tr.t) && tr.t.length > 0);
    if (tracks.length < 3) return null;
    // Common time grid — use the longest track's t-vector.
    const longest = tracks.reduce(
        (a, b) => (a.t.length >= b.t.length ? a : b),
    );
    const grid = longest.t.slice();
    const series = grid.map((t, i) => {
        const samples_at_t = tracks
            .map((tr) => tr.z[Math.min(i, tr.z.length - 1)])
            .filter((v) => Number.isFinite(v));
        if (!samples_at_t.length) return null;
        samples_at_t.sort((a, b) => a - b);
        const lo = samples_at_t[Math.floor(samples_at_t.length * 0.05)];
        const hi = samples_at_t[Math.ceil(samples_at_t.length * 0.95) - 1];
        return [t, lo, hi];
    }).filter(Boolean);
    if (series.length < 2) return null;
    // Plotly 3D doesn't have a direct envelope; we render a coloured
    // ribbon by stacking two scatter3d lines with fill and a soft
    // mesh3d if many samples, but a thick semi-transparent line per
    // bound is the lightweight option that always renders.
    const x = longest.x.slice(0, series.length);
    const y = longest.y.slice(0, series.length);
    const lo = series.map(([, l]) => l);
    const hi = series.map(([, , h]) => h);
    return {
        type: "scatter3d",
        mode: "lines",
        name: "Pk uncertainty cloud (alt p5–p95)",
        x: [...x, ...x.slice().reverse()],
        y: [...y, ...y.slice().reverse()],
        z: [...lo, ...hi.slice().reverse()],
        line: { color: CLOUD_COLOR, width: 8 },
        opacity: 0.35,
        showlegend: true,
    };
}
